/**
 * Local Library provider (spec section 7 - "Local").
 *
 * This is the default provider and the reason BEX LOOP works offline.
 * It does not generate anything: it owns importing, validating and
 * serving clips that already exist on the device.
 */

import { VideoProvider, PROVIDER_STATUS } from './base.js';

export class LocalVideoProvider extends VideoProvider {
  constructor({ assetStore } = {}) {
    super({
      id: 'local-library',
      name: 'Local Library',
      type: 'local',
      description:
        'Clips you already have. Import a file (including one you generated manually in Google Vids) and it becomes a reusable scene asset. Works with no internet and no API key.',
      docsUrl: null,
      capabilities: {
        type: 'local',
        automated: false,
        requiresApiKey: false,
        requiresWorker: false,
        manualStepRequired: true,
        asynchronous: false,
        supportsCancel: false,
        nativeAudio: true,
        durations: [],
        aspectRatios: ['9:16', '1:1', '16:9'],
        resolutions: [],
        costModel: 'none',
        costNote: 'Free. Storage is your own device.',
        browserAutomation: false,
      },
    });
    this.assetStore = assetStore;
  }

  async status() {
    let count = 0;
    let bytes = 0;
    try {
      const summary = await this.assetStore?.assetFootprint?.();
      count = summary?.count ?? 0;
      bytes = summary?.bytes ?? 0;
    } catch {
      /* storage may be unavailable */
    }
    return {
      state: PROVIDER_STATUS.READY,
      detail: count ? `${count} clip${count === 1 ? '' : 's'} stored on this device.` : 'No clips imported yet.',
      checkedAt: new Date().toISOString(),
      meta: { assetCount: count, bytes },
    };
  }

  async validateCredentials() {
    return { configured: true, detail: 'No credentials required.' };
  }

  /**
   * There is nothing to generate. Returning a clear, non-fake answer is
   * more useful than throwing.
   */
  async generate() {
    return {
      mode: 'import',
      providerId: this.id,
      manualStepRequired: true,
      detail: 'The Local Library provider imports existing files. Use the import step instead.',
      steps: [
        'Choose a scene.',
        'Import a video file from this device.',
        'Review the validation result.',
        'Mark the loop quality and save it to the library.',
      ],
    };
  }

  async getGenerationStatus() {
    return { state: 'NOT_APPLICABLE', detail: 'Local imports complete immediately.', done: true };
  }
}
